import { useQuery } from '@tanstack/react-query';

import { getAccommodationOffersMap, getBedbankRoomsById } from '~/components/Tours/utils/accommodationAddOns';

import SearchService, { FlashLpcOfferTypes } from '~/services/SearchService';

export const tourHotelAddOnQueryKeys = {
  all: ['tour-hotel-add-ons'] as const,
  offerNames: (ids: Array<string>) => [...tourHotelAddOnQueryKeys.all, 'offer-names', [...ids].sort()] as const,
  bedbankRooms: (propertyId: string) => [...tourHotelAddOnQueryKeys.all, 'bedbank-rooms', propertyId] as const,
  leOffers: () => [...tourHotelAddOnQueryKeys.all, 'le-offers'] as const,
};

/** The names of recommended Flash, LPC and bedbank offers, by id. */
export function useGetHotelOfferNames(ids: Array<string>) {
  return useQuery({
    queryKey: tourHotelAddOnQueryKeys.offerNames(ids),
    queryFn: async () => {
      const offers = await getAccommodationOffersMap(ids);
      return Object.fromEntries(
        Object.entries(offers).map(([id, offer]) => [id, (offer as { name?: string }).name ?? id]),
      );
    },
    enabled: ids.length > 0,
    staleTime: Infinity,
  });
}

/** The rooms a bedbank property offers, to pick the one recommended. */
export function useGetBedbankRooms(propertyId: string) {
  return useQuery({
    queryKey: tourHotelAddOnQueryKeys.bedbankRooms(propertyId),
    queryFn: async (): Promise<Array<{ id: string; name: string }>> => await getBedbankRoomsById(propertyId),
    staleTime: Infinity,
  });
}

/** Live Flash and LPC hotel offers, to pick one by its offer id. */
export function useGetLeHotelOffers() {
  return useQuery({
    queryKey: tourHotelAddOnQueryKeys.leOffers(),
    queryFn: async ({ signal }): Promise<Array<{ id: string; type: string }>> =>
      (await SearchService.getSearchLightOffers({ offerTypes: FlashLpcOfferTypes, signal })).map((offer) => ({
        id: offer.id,
        type: offer.type,
      })),
    staleTime: Infinity,
  });
}
